<template>
  <ModalContainer
    class="manage-views-modal"
    :title="t('dataGrid.views.manage.title')"
    icon="ContentBox"
    :open="open"
    :has-footer="true"
    :show-cancel-button="true"
    :cancel-button-text="t('dataGrid.views.manage.close')"
    :data-qa="manageViewsDataQa.modalTitle"
    :data-qa-close="manageViewsDataQa.closeIcon"
    :data-qa-cancel-button="manageViewsDataQa.closeBtn"
    @on-close="viewsStore.closeManageModal"
  >
    <ManageableList
      :items="items"
      :rename-placeholder="t('dataGrid.views.modal.nameLabel')"
      :rename-error-text="t('dataGrid.views.modal.duplicateError')"
      :rename-validator="renameValidator"
      :save-tooltip="t('common.save')"
      :cancel-label="t('common.cancel')"
      :data-qa="manageViewsDataQa.list"
      @toggle:favorite="handleToggleFavorite"
      @rename="handleRename"
      @duplicate="handleDuplicate"
      @delete="requestDelete"
      @reorder="handleReorder"
    />
    <div class="manage-views-add-new">
      <ButtonItem
        theme="link"
        icon-position="left"
        left-icon="AddCircleOutline"
        :text="t('dataGrid.views.createNewView')"
        :is-disabled="!hasDefaultView"
        :data-qa="manageViewsDataQa.addNewViewBtn"
        @click="handleCreateNewView"
      />
    </div>
  </ModalContainer>
  <ModalContainer
    :title="t('dataGrid.views.manage.deleteConfirm.title')"
    icon="Delete"
    :open="isDeleteConfirmOpen"
    :primary-button="deleteConfirmButtonArgs"
    :cancel-button-text="t('dataGrid.views.manage.deleteConfirm.cancel')"
    :data-qa="manageViewsDataQa.deleteConfirmModal"
    :data-qa-primary-button="manageViewsDataQa.deleteConfirmBtn"
    :data-qa-cancel-button="manageViewsDataQa.deleteCancelBtn"
    @on-close="cancelDelete"
    @primary-button-click="confirmDelete"
  >
    <p class="manage-views-delete-message body-regular-default">
      {{ t('dataGrid.views.manage.deleteConfirm.message', { name: deleteTargetName }) }}
    </p>
  </ModalContainer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { ButtonItem, ManageableList, ModalContainer } from '@lw-design-system/ui';
import { useViewsStore } from '@/stores/views-store';
import { useApiErrorSnackbar } from '@/composables/common/useApiErrorSnackbar';
import { MAX_VIEW_NAME_LENGTH } from '@/composables/common/data-grid/useViewNameValidation';
import { useStore } from '@/stores/store';
import { manageViewsDataQa } from '~/constants/commons/data-qa';
import { getViewDisplayName } from '~/utils/view-display-name';
import type { ActionMeta, SavedView, SnackbarMessage } from '~/types';

const { t } = useI18n();
const { add } = useSnackbar();
const { notifyApiError } = useApiErrorSnackbar();

defineProps<{ open: boolean }>();

const viewsStore = useViewsStore();
const { views, defaultView } = storeToRefs(viewsStore);
const { loadArray } = storeToRefs(useStore());

const hasDefaultView = computed(() => Boolean(defaultView.value));

const handleCreateNewView = () => {
  viewsStore.closeManageModal();
  viewsStore.createViewFromDefault();
};

const isOwn = (view: SavedView) => view.scope === 'own';
const isGlobal = (view: SavedView) => view.scope === 'global';

const disabledTooltipFor = (view: SavedView) =>
  isGlobal(view)
    ? t('dataGrid.views.manage.disabledTooltips.globalView')
    : t('dataGrid.views.manage.disabledTooltips.sharedView');

const buildActionMeta = (
  view: SavedView,
  isDisabled: boolean,
  enabledTooltip: string
): ActionMeta => ({
  isDisabled,
  tooltip: isDisabled ? disabledTooltipFor(view) : enabledTooltip
});

const buildFavoriteMeta = (view: SavedView): ActionMeta => {
  const isDisabled = !isOwn(view);
  const enabledTooltip = view.isDefault
    ? t('dataGrid.views.manage.actions.unsetFavorite')
    : t('dataGrid.views.manage.actions.setFavorite');
  return buildActionMeta(view, isDisabled, enabledTooltip);
};

const items = computed(() =>
  views.value.map((view) => ({
    id: view.id,
    name: getViewDisplayName(view, t),
    isFavorite: view.isDefault,
    drag: buildActionMeta(view, !isOwn(view), t('dataGrid.views.manage.actions.drag')),
    favorite: buildFavoriteMeta(view),
    rename: buildActionMeta(view, !isOwn(view), t('dataGrid.views.manage.actions.rename')),
    duplicate: buildActionMeta(view, isGlobal(view), t('dataGrid.views.manage.actions.duplicate')),
    delete: buildActionMeta(view, !isOwn(view), t('dataGrid.views.manage.actions.delete'))
  }))
);

const renameValidator = (value: string, id: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_VIEW_NAME_LENGTH) return false;
  return !views.value.some(
    (view) => view.id !== id && view.name.trim().toLowerCase() === trimmed.toLowerCase()
  );
};

const deleteTargetId = ref<string | null>(null);
const isDeleteConfirmOpen = computed(() => deleteTargetId.value !== null);
const deleteTargetName = computed(
  () => views.value.find((view) => view.id === deleteTargetId.value)?.name ?? ''
);
const isDeletingView = computed(() => loadArray.value.includes('deleteView'));
const deleteConfirmButtonArgs = computed(() => ({
  text: t('dataGrid.views.manage.deleteConfirm.confirm'),
  isDisabled: isDeletingView.value,
  isLoading: isDeletingView.value
}));

const handleToggleFavorite = async (id: string) => {
  const view = views.value.find((candidate) => candidate.id === id);
  try {
    await viewsStore.manageSetFavoriteView(id);
    add({
      type: 'success',
      message: t('dataGrid.views.manage.setFavoriteSuccess', { name: getViewDisplayName(view, t) })
    } as SnackbarMessage);
  } catch (error: unknown) {
    notifyApiError(error);
  }
};

const handleRename = async ({ id, name }: { id: string; name: string }) => {
  try {
    await viewsStore.manageRenameView(id, name);
    add({
      type: 'success',
      message: t('dataGrid.views.manage.renameSuccess', { name })
    } as SnackbarMessage);
  } catch (error: unknown) {
    notifyApiError(error);
  }
};

const handleDuplicate = async (id: string) => {
  try {
    const created = await viewsStore.manageDuplicateView(id);
    add({
      type: 'success',
      message: t('dataGrid.views.manage.duplicateSuccess', { name: created.name })
    } as SnackbarMessage);
  } catch (error: unknown) {
    notifyApiError(error);
  }
};

const handleReorder = async ({ items: reorderedItems }: { items: { id: string }[] }) => {
  const orderedIds = reorderedItems
    .map((item) => views.value.find((view) => view.id === item.id))
    .filter((view): view is SavedView => view !== undefined && view.scope === 'own')
    .map((view) => view.id);

  try {
    await viewsStore.manageReorderViews(orderedIds);
  } catch (error: unknown) {
    notifyApiError(error, { message: t('dataGrid.views.manage.reorderError') });
  }
};

const requestDelete = (id: string) => {
  deleteTargetId.value = id;
};

const cancelDelete = () => {
  deleteTargetId.value = null;
};

const confirmDelete = async () => {
  const id = deleteTargetId.value;
  if (!id) return;
  const name = deleteTargetName.value;

  try {
    await viewsStore.manageDeleteView(id);
    add({
      type: 'success',
      message: t('dataGrid.views.manage.deleteSuccess', { name })
    } as SnackbarMessage);
  } catch (error: unknown) {
    notifyApiError(error, { message: t('dataGrid.views.manage.deleteSharedError') });
  } finally {
    deleteTargetId.value = null;
  }
};
</script>

<style scoped>
.manage-views-delete-message {
  color: var(--text-default);
}

.manage-views-add-new {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}
</style>
